import { css } from '@emotion/react'
import { patchUserTips, useUserTips } from 'api/goodtrust/user'
import { Button } from 'components/button/Button'
import { AnimateActions } from 'components/content/memory/memoryToolBar/AnimateActions'
import {
  SAnimationContainer,
  SDisappearingButtonText,
  SDropdownContainer,
  SIconButton,
  SMemoryToolBar,
  SShareTip,
  STag,
  STitle,
  STools,
} from './MemoryToolBar.styled'
import { RemainingAnimations } from 'components/content/memory/remainingAnimations/RemainingAnimations'
import { useChooseSongModal } from 'components/content/singingPortraits/chooseSongModal/ChooseSongModal'
import { Icon } from 'components/icon'
import { useChooseAnimationModal } from 'components/memory/chooseAnimationModal/ChooseAnimationModal'
import { AnimationDriver, MemoryType, SelectedMemory } from 'components/memory/MemoryUtils'
import { useInfoSideModal } from 'components/modal/infoSideModal/InfoSideModal'
import { usePopperModal } from 'components/modal/PopperModal'
import { usePopup } from 'components/popup/Popup'
import { MemoryShareItems } from 'components/share/MemoryShareItems'
import { useStoryPortraitLogic } from 'components/storyPortraits/logic'
import { handleAndToastError, toastSuccess } from 'components/Toast'
import { format as formatDateFns, parseISO } from 'date-fns'
import { useTranslation } from 'next-i18next'
import { useRef, useState } from 'react'
import { fireEvent } from 'utils/analytics'
import { colors } from 'utils/colors'
import { formatDate } from 'utils/date'
import { unwrapResponse } from 'utils/fetcher'
import { usePolyfilledDownload } from 'utils/file'
import { formatFileSize } from 'utils/format'
import { useLoadingCallback, useWindowSize } from 'utils/hooks'
import { BREAKPOINTS, pxToRem } from 'utils/styled'

const getInfoList = (memory?: SelectedMemory) => {
  if (!memory) return []
  const originalFile = memory.original_file
  const name = memory.name ?? undefined
  const resolution = `${originalFile?.width} x ${originalFile?.height}`
  const size = formatFileSize(originalFile?.file_size || 0)
  let createdDate
  if (memory?.created) {
    createdDate = parseISO(memory?.created)
  }
  return [
    {
      icon: <Icon name="Calendar" color={colors.gray[500]} />,
      title: createdDate && formatDate(createdDate, 'medium'),
      description: createdDate && formatDateFns(createdDate, 'E, HH:mm'),
    },
    {
      icon: <Icon name="File" color={colors.gray[500]} />,
      title: name,
      description: `${resolution}, ${size}`,
    },
  ]
}

export function MemoryToolBar(props: {
  memory?: SelectedMemory
  onAnimate?: (driver: AnimationDriver) => void
  isAnimating?: boolean
  className?: string
  setSelectedMemory: (val: SelectedMemory) => void
  onDelete: () => void
  onShare: () => void
}) {
  const { t } = useTranslation('life_stories')
  const [isDropdownOpen, setDropdown] = useState(false)
  const [infoModal, openInfoModal] = useInfoSideModal({
    title: t('life_stories.inside_page.memory_viewer.toolbar_info_modal_title'),
    infoList: getInfoList(props.memory),
  })
  const ref = useRef<HTMLDivElement>(null)
  const { isDesktop } = useWindowSize()

  const [chooseAnimationModal, chooseAnimationViaModal] = useChooseAnimationModal()
  const [chooseSongModal, chooseSongViaModal] = useChooseSongModal()
  const storyLogic = useStoryPortraitLogic()

  const { data: tipData, mutate: mutateTipData } = useUserTips()

  const downloadFile = usePolyfilledDownload()

  const [requestAnimation, isRequestingAnimation] = useLoadingCallback(
    async (type: MemoryType['type']) => {
      let driver
      if (type === 'LIVE_PORTRAIT') {
        driver = await chooseAnimationViaModal()
      } else {
        driver = await chooseSongViaModal()
      }
      if (!driver) {
        toastSuccess('animation_cancelled')
        return
      }
      await props.onAnimate?.(driver)
    }
  ) as [(type: MemoryType['type']) => Promise<void>, boolean, boolean]

  const [downloadOriginal, isDownloadingOriginal] = useLoadingCallback(async () => {
    if (!props.memory?.original_file) return
    await downloadFile(props.memory.original_file)
    if (props.memory?.selectedAnimation?.animation_type === 'STORY_PORTRAIT') {
      storyLogic.onShared('download')
    } else {
      fireEvent(
        props.memory?.selectedAnimation?.animation_type === 'LIVE_PORTRAIT'
          ? 'animation_shared'
          : 'sh_animation_shared',
        {
          platform: 'download',
          driver: 'none',
        }
      )
    }
  })

  const [downloadAnimation, isDownloadingAnimation] = useLoadingCallback(async () => {
    if (!props.memory?.selectedAnimation?.file || !downloadFile) return
    await downloadFile(props.memory.selectedAnimation.file)

    if (props.memory?.selectedAnimation?.animation_type === 'STORY_PORTRAIT') {
      storyLogic.onShared('download')
    } else {
      fireEvent(
        props.memory?.selectedAnimation?.animation_type === 'LIVE_PORTRAIT'
          ? 'animation_shared'
          : 'sh_animation_shared',
        {
          platform: 'download',
          driver: props.memory.selectedAnimation.driver as string,
        }
      )
    }
  })

  const sharePopup = usePopup(
    <SShareTip>
      {isDesktop ? (
        <MemoryShareItems memory={props.memory} isModal={true} />
      ) : (
        <Button type="button" size="large" block onClick={props.onShare}>
          {t('life_stories.inside_page.memory_viewer.popup_share_animation_button')}
        </Button>
      )}
    </SShareTip>,
    {
      onClose: async () => {
        try {
          await patchUserTips({ hide_memory_share_tip: true }).then(unwrapResponse)
          await mutateTipData()
        } catch (err) {
          handleAndToastError(err)
        }
      },
      title: isDesktop
        ? t('life_stories.inside_page.memory_viewer.share_animation_title_desktop')
        : t('life_stories.inside_page.memory_viewer.share_animation_title_mobile'),
      show:
        !!tipData?.json &&
        !tipData?.json?.hide_memory_share_tip &&
        props.memory?.selectedAnimation?.status === 'DONE',
      popperConfig: {
        placement: 'bottom-start',
      },
    }
  )

  const noFace = !!props.memory?.animated_files?.some((a) => a.error_type === 'FACE_ERROR')
  const hasLivePortrait = !!props.memory?.animated_files?.some(
    (a) => a.animation_type === 'LIVE_PORTRAIT'
  )

  const animationType = props.memory?.selectedAnimation?.animation_type
  const isSingingPortrait = animationType === 'SINGING_PORTRAIT'
  const shouldRenderSPAction = isSingingPortrait || !animationType
  const shouldRenderLPAction = !hasLivePortrait && !animationType

  const singingPortraitAction = () => {
    closeMenu()
    requestAnimation('SINGING_PORTRAIT')
  }
  const livingPortraitAction = () => {
    closeMenu()
    requestAnimation('LIVE_PORTRAIT')
  }

  const shouldRenderAnimateActions = !noFace && (!animationType || isSingingPortrait)
  const shouldRenderActionDropdown = !isDesktop && shouldRenderSPAction && shouldRenderLPAction

  const animateActions = (
    <AnimateActions
      inDropdown={shouldRenderActionDropdown}
      isSingingPortrait={isSingingPortrait}
      singingPortraitAction={shouldRenderSPAction ? singingPortraitAction : undefined}
      livingPortraitAction={shouldRenderLPAction ? livingPortraitAction : undefined}
    />
  )

  const {
    modal: animateActionsModal,
    setReferenceElement,
    open: openAnimateActionsModal,
    close: closeMenu,
  } = usePopperModal(() => animateActions, {
    placement: 'bottom',
    offset: 16,
  })

  // IMRPOVE: convert to i18n
  return (
    <SMemoryToolBar className={props.className} ref={ref}>
      {infoModal}
      {sharePopup.popup}
      {chooseAnimationModal}
      {chooseSongModal}
      {animateActionsModal}
      {!isRequestingAnimation && !props.isAnimating && (
        <STools>
          <SAnimationContainer>
            {animationType && animationType !== 'STORY_PORTRAIT' ? (
              <SIconButton
                type="button"
                variant="text"
                leftIcon={<Icon name="File" />}
                onClick={() =>
                  props.memory &&
                  props.setSelectedMemory({ ...props.memory, selectedAnimation: null })
                }
              >
                {isDesktop ? 'Show original' : ''}
              </SIconButton>
            ) : (
              <RemainingAnimations
                css={css`
                  @media ${BREAKPOINTS.LG.max} {
                    display: none;
                  }
                  margin-right: ${pxToRem(24)}rem;
                `}
              />
            )}
            {shouldRenderAnimateActions ? (
              shouldRenderActionDropdown ? (
                <SIconButton
                  type="button"
                  variant="text"
                  ref={setReferenceElement}
                  onClick={openAnimateActionsModal}
                  leftIcon={<Icon name="CaretDown" />}
                >
                  <STag>NEW</STag>
                  Animate
                </SIconButton>
              ) : (
                animateActions
              )
            ) : null}
          </SAnimationContainer>
          <SIconButton
            type="button"
            variant="text"
            onClick={props.onShare}
            data-testid="library-share-button"
            leftIcon={<Icon name="Share" />}
            ref={sharePopup.setReferenceElement}
          >
            <SDisappearingButtonText>Share</SDisappearingButtonText>
          </SIconButton>
          <SIconButton
            type="button"
            variant="text"
            onClick={openInfoModal}
            leftIcon={<Icon name="InfoCircle" />}
            css={css`
              margin-right: ${pxToRem(12)}rem;
              @media ${BREAKPOINTS.MD.max} {
                margin-right: 0;
              }
            `}
          >
            <SDisappearingButtonText>Info</SDisappearingButtonText>
          </SIconButton>
          <Button
            type="button"
            variant="text"
            data-testid="library-menu-button"
            onClick={() => setDropdown(!isDropdownOpen)}
          >
            <Icon name="MoreInfo" color="white" />
          </Button>
          {isDropdownOpen && (
            <SDropdownContainer>
              {props.memory?.selectedAnimation?.animation_type !== 'STORY_PORTRAIT' && (
                <Button
                  type="button"
                  variant="text"
                  tone="dark"
                  onClick={downloadOriginal}
                  leftIcon={<Icon name="File" />}
                  loading={isDownloadingOriginal}
                >
                  Download original
                </Button>
              )}

              {props.memory?.selectedAnimation && (
                <Button
                  type="button"
                  variant="text"
                  tone="dark"
                  data-testid="download-button"
                  onClick={downloadAnimation}
                  leftIcon={<Icon name="FileDownloadFrame" />}
                  loading={isDownloadingAnimation}
                >
                  Download animation
                </Button>
              )}
              <Button
                type="button"
                variant="text"
                tone="danger"
                onClick={props.onDelete}
                leftIcon={<Icon name="Bin" />}
              >
                Delete
              </Button>
            </SDropdownContainer>
          )}
        </STools>
      )}
      <STitle memoryUuid={props.memory?.uuid} />
    </SMemoryToolBar>
  )
}
